import { isValidElement, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

/** A column header: plain node, or {label, className} for per-column classes. */
export type TableColumn = ReactNode | { label: ReactNode; className?: string }

function colParts(col: TableColumn): { label: ReactNode; className?: string } {
  if (typeof col === 'object' && col !== null && !isValidElement(col) && !Array.isArray(col) && 'label' in col) {
    return col as { label: ReactNode; className?: string }
  }
  return { label: col as ReactNode }
}

/* Icewall table: no header fill, h-12 medium slate-500 headers, hairline rows, hover tint.
   Every column stays visible on phones - the table scrolls sideways with tighter
   mobile padding, and a right-edge fade hints that more columns are off-screen. */
export function Table({ columns, children, className }: { columns: TableColumn[]; children: ReactNode; className?: string }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const frame = useRef(0)
  // Whether the table is wider than its card, and whether there is more of it
  // off the right edge right now. The first decides if this is a scroll
  // container at all, the second draws the fade.
  const [overflowing, setOverflowing] = useState(false)
  const [moreRight, setMoreRight] = useState(false)

  const measure = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    // scrollWidth reports overflowing content whether or not the box scrolls,
    // so the same comparison works in both states.
    const over = el.scrollWidth > el.clientWidth + 1
    setOverflowing(over)
    setMoreRight(over && el.scrollLeft + el.clientWidth < el.scrollWidth - 8)
  }, [])

  // Reading layout in the scroll handler itself forced a synchronous reflow on
  // every wheel event; a frame's delay is invisible on a fade hint.
  const onScroll = useCallback(() => {
    cancelAnimationFrame(frame.current)
    frame.current = requestAnimationFrame(measure)
  }, [measure])

  // Before paint, so an overflowing table never shows a frame spilling out of
  // its card before the scroll container is applied.
  useLayoutEffect(measure)

  useEffect(() => {
    const el = scrollRef.current
    const table = tableRef.current
    if (!el || !table) return
    // Catches column widths changing, not just the window - and unlike the old
    // [children] effect it subscribes once instead of on every render.
    const observer = new ResizeObserver(onScroll)
    observer.observe(el)
    observer.observe(table)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(frame.current)
    }
  }, [onScroll])

  return (
    // Positioning lives on this wrapper - .box forces its own position (see index.css gotcha)
    <div className={cn('relative', className)}>
      {/* Only a scroll container when it has something to scroll. overflow-x
          on either axis makes the box scrollable on BOTH, so a wheel gesture
          used to latch onto a table that could not move and only then chain to
          the page - which is what made scrolling over any table feel sticky. */}
      <div ref={scrollRef} onScroll={onScroll} className={cn('box', overflowing && 'overflow-x-auto')}>
        <table ref={tableRef} className="w-full text-left text-sm">
          <thead>
            <tr>
              {columns.map((col, i) => {
                const { label, className: colClass } = colParts(col)
                return (
                  <th
                    key={i}
                    className={cn('h-12 px-2 text-left align-middle text-sm font-medium text-foreground/60 sm:px-4', colClass)}
                  >
                    {label}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
      {moreRight && (
        <div className="pointer-events-none absolute inset-y-px right-px w-10 rounded-r-2xl bg-gradient-to-l from-foreground/15 to-transparent" />
      )}
    </div>
  )
}

/* The trailing column of row buttons. Auto table layout hands surplus width to
   every column, so an actions column left to itself grew far wider than its
   buttons and the header drifted to the table's right edge, nowhere near the
   first button it names. width:1px makes it hug its content instead - the
   surplus goes to the columns that can use it, and the header lands on the
   first button. Put the class on the Cell as well as the column. */
export const actionsCellClass = 'w-px whitespace-nowrap'
export const actionsColumn: TableColumn = { label: 'Actions', className: actionsCellClass }

export function Row({ children, className, onClick }: { children: ReactNode; className?: string; onClick?: () => void }) {
  return (
    <tr
      className={cn('border-t border-foreground/10 transition-colors hover:bg-foreground/5', onClick && 'cursor-pointer', className)}
      onClick={onClick}
    >
      {children}
    </tr>
  )
}

export function Cell({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={cn('p-2 align-middle sm:p-4', className)}>{children}</td>
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children?: ReactNode }) {
  return (
    <tr className="border-t border-foreground/10">
      <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-foreground/60">
        {children ?? 'Nothing here yet.'}
      </td>
    </tr>
  )
}
